import * as Sentry from '@sentry/react';
import { Navigate, createBrowserRouter } from 'react-router-dom';

import type { AppDefinition } from '@/platform/app/contracts';
import { DashboardShell } from '@/platform/shell/DashboardShell';

import { AuthNavigation } from './AuthNavigation';
import { PrivateRoute } from './PrivateRoute';
import { PublicRoute } from './PublicRoute';
import { ROUTES } from './routes';

/**
 * Emits the pageload and navigation transactions Sentry's tracing is built on.
 * A no-op until `Sentry.init` runs with the router tracing integration, so
 * Storybook and a DSN-less build are unaffected.
 */
const createTracedBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

/**
 * Root router of a dashboard: public auth area, the private shell (whose
 * child routes come from the app's modules) and any app-specific top-level
 * routes (e.g. Tenko's `/oauth/*` consent page).
 */
export const createAppRouter = (app: AppDefinition) =>
  createTracedBrowserRouter([
    {
      path: `${ROUTES.auth}/*`,
      element: (
        <PublicRoute>
          <AuthNavigation />
        </PublicRoute>
      ),
    },
    {
      path: `${ROUTES.app}/*`,
      element: (
        <PrivateRoute to={ROUTES.auth}>
          <DashboardShell />
        </PrivateRoute>
      ),
    },
    ...(app.topLevelRoutes ?? []),
    {
      path: '/*',
      element: <Navigate to={ROUTES.auth} replace />,
    },
  ]);
